import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/hooks/useStore";
import { CheckCircle2, AlertTriangle, Loader2, LogOut, Shield } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  to: string;
  visibility: "always" | "admin" | "player";
}

const NAV_ITEMS: NavItem[] = [
  { label: "All Cards", to: "/cards", visibility: "always" },
  { label: "Add Card", to: "/cards/add", visibility: "admin" },
  { label: "Decks", to: "/decks", visibility: "admin" },
  { label: "Playtest", to: "/playtest", visibility: "player" },
  { label: "Reference Data", to: "/reference", visibility: "admin" },
  { label: "Glossary", to: "/glossary", visibility: "always" },
  { label: "Import / Export", to: "/import-export", visibility: "admin" },
  { label: "Admin Panel", to: "/admin", visibility: "admin" },
];

function SaveIndicator() {
  const { saveStatus } = useStore();
  if (saveStatus === "idle") return null;
  if (saveStatus === "saving")
    return (
      <span className="flex items-center gap-1 text-xs text-[#64748b]">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  if (saveStatus === "saved")
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-400">
        <CheckCircle2 className="h-3 w-3" /> Saved
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-xs text-red-400">
      <AlertTriangle className="h-3 w-3" /> Error
    </span>
  );
}

export function NavBar() {
  const { user, profile, signOut } = useAuth();
  const isAdmin = profile?.role === "admin";

  const items = NAV_ITEMS.filter((it) => {
    if (it.visibility === "always") return true;
    if (it.visibility === "admin") return isAdmin;
    if (it.visibility === "player") return Boolean(user);
    return false;
  });

  return (
    <header className="border-b border-[#2a2f3a] bg-[#0f1115]/80 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber" />
          <span className="font-display text-base font-bold tracking-wide">GUILD MASTERS</span>
        </Link>

        <nav className="hidden flex-1 items-center gap-1 md:flex">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                cn(
                  "rounded px-2.5 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors",
                  isActive
                    ? "bg-amber/15 text-amber-soft"
                    : "text-[#94a3b8] hover:bg-[#22262f] hover:text-white",
                )
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <SaveIndicator />
          {user ? (
            <>
              <span className="hidden text-xs text-[#94a3b8] sm:inline">
                {profile?.display_name ?? user.email}
                {isAdmin && (
                  <span className="ml-1 rounded bg-amber/15 px-1 py-0.5 text-[10px] text-amber-soft">
                    admin
                  </span>
                )}
              </span>
              <button
                onClick={() => signOut()}
                className="text-[#64748b] hover:text-white"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Link to="/signin" className="gm-link text-xs">
              Sign in
            </Link>
          )}
        </div>
      </div>

      <nav className="md:hidden flex flex-wrap items-center gap-1 border-t border-[#2a2f3a] px-4 py-2 text-xs">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              cn(
                "rounded px-2 py-1",
                isActive ? "bg-amber/15 text-amber-soft" : "text-[#94a3b8]",
              )
            }
          >
            {it.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
